<template>
  <div class="flowbuilder-node-container">
    <BaseNode
      :node="node"
      :icon="nodeIcon"
      :metrics="metrics"
      :mini-metrics="true"
      @click="emit('click')"
      @delete-node="emit('delete-node')"
      classes="bg-rose-50 ring-rose-400/50 dark:bg-rose-950 dark:ring-rose-500/50 does-terminate">
      <template #context>Redirect to another webpage:</template>
      <template v-if="node.url">
        <div class="truncate text-sm">
          {{ node.url }}
          <div v-if="node.open" class="mt-1 text-xs text-gray-500">
            (opens in new tab)
          </div>
        </div>
      </template>
      <template v-else>Enter a redirect URL</template>
    </BaseNode>
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs, inject } from 'vue'
import BaseNode from './BaseNode.vue'
import { getNodeIcon } from '../../utils/nodeIcons'
import type { NodeRedirect } from '../../types/nodes'

/**
 * RedirectNode Component
 *
 * Redirects the visitor to another webpage.
 *
 * Features:
 * - Redirects to external URLs
 * - Option to open in new tab or same window
 * - URL validation
 * - Terminal node (ends the flow)
 *
 * Props:
 * - node: The redirect node configuration with url and open settings
 */

interface Props {
  node: NodeRedirect
}

const props = defineProps<Props>()
const emit = defineEmits(['delete-node', 'click'])

const { node } = toRefs(props)

const getNodeMetrics = inject<(node: any) => any>('getNodeMetrics', () => null)

// Get the appropriate icon for this node
const nodeIcon = computed(() => {
  return getNodeIcon(props.node)
})

// Get metrics for this node
const metrics = computed(() => {
  return getNodeMetrics(props.node)
})

/**
 * Validate the URL format
 */
const isValidUrl = computed(() => {
  const urlPattern = /^https?:\/\/.+/i
  return urlPattern.test(node.value.url)
})
</script>

